/**
 * Shared chat UI components used by ChatCoach.
 */
import React from 'react';
import { Link } from 'react-router';
import { Icons } from '@shared/constants';

export const MAX_MESSAGE_LENGTH = 1000;

/** Render simple markdown (bold, bullets) as React elements. */
export function renderMarkdown(text: string, onNavigate?: () => void): React.ReactNode {
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Bullet lines: "* text", "- text", "• text"
    const bulletMatch = line.match(/^\s*[*\-•]\s+(.+)/);
    if (bulletMatch) {
      elements.push(
        <div key={i} className="flex gap-1.5 ml-1">
          <span className="shrink-0">•</span>
          <span>{renderInline(bulletMatch[1], onNavigate)}</span>
        </div>
      );
      continue;
    }

    // Regular line
    if (line.trim()) {
      elements.push(<span key={i}>{renderInline(line, onNavigate)}</span>);
    }

    // Add line break between non-empty lines (but not after the last)
    if (i < lines.length - 1 && line.trim()) {
      elements.push(<br key={`br-${i}`} />);
    }
  }

  return <>{elements}</>;
}

/** Render inline markdown: **bold** and [links](/path) */
export function renderInline(text: string, onNavigate?: () => void): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g);
  return parts.map((part, i) => {
    const boldMatch = part.match(/^\*\*(.+)\*\*$/);
    if (boldMatch) {
      return <strong key={i}>{boldMatch[1]}</strong>;
    }
    const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch) {
      const href = linkMatch[2];
      const label = linkMatch[1];
      // Internal links use React Router for SPA navigation (no page reload)
      if (href.startsWith('/')) {
        return (
          <Link key={i} to={href} onClick={onNavigate} className="underline font-medium hover:text-growth transition-colors">
            {label}
          </Link>
        );
      }
      return (
        <a key={i} href={href} target="_blank" rel="noopener noreferrer" className="underline font-medium hover:text-growth transition-colors">
          {label}
        </a>
      );
    }
    return part;
  });
}

export function ChatHeader({ onClose, actions }: { onClose?: () => void; actions?: React.ReactNode }) {
  return (
    <div className="bg-sand p-4 border-b border-mist/20 flex items-center justify-between" style={{ paddingTop: 'calc(var(--sat) + 1rem)' }}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-growth flex items-center justify-center shadow-xs">
          <Icons.Sparkles className="text-cloud w-5 h-5" />
        </div>
        <div>
          <h3 className="font-serif text-growth font-medium">Quick chat</h3>
          <p className="text-xs text-growth/70">Wellness Assistant</p>
        </div>
      </div>
      <div className="flex items-center gap-1">
        {actions}
        {onClose && (
          <button
            onClick={onClose}
            aria-label="Close chat"
            className="p-2 text-growth hover:text-growth bg-white/50 rounded-full hover:bg-white transition-all"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
        )}
      </div>
    </div>
  );
}
